<script setup>
// SiteUp: "Configurar com ajuda de IA". Mostra um meta-prompt pronto que o cliente
// copia e cola numa IA externa (ChatGPT/Claude) para gerar a configuração do agente
// (prompt principal, cenários, proteções, FAQs) + recomendações. Ver
// helpers/configMetaPrompt.js.
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { buildConfigMetaPrompt } from 'dashboard/routes/dashboard/captain/helpers/configMetaPrompt';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const dialogRef = ref(null);

const metaPrompt = computed(() =>
  buildConfigMetaPrompt({
    assistantName: props.assistant?.name,
    productName: props.assistant?.config?.product_name,
  })
);

const copyPrompt = async () => {
  await copyTextToClipboard(metaPrompt.value);
  useAlert('Meta-prompt copiado! Cole numa IA (ChatGPT, Claude…) e siga os passos.');
};

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    title="Configurar com ajuda de IA"
    description="Copie o texto abaixo, cole em uma IA (ChatGPT, Claude, Gemini…) e responda às perguntas. Ela vai te devolver o prompt principal, cenários, proteções e FAQs prontos para colar aqui — e dizer o que ativar."
    :show-confirm-button="false"
    cancel-button-label="Fechar"
  >
    <div class="flex flex-col gap-4">
      <ol
        class="flex flex-col gap-1 text-sm text-n-slate-11 list-decimal ltr:pl-5 rtl:pr-5"
      >
        <li>Clique em "Copiar meta-prompt".</li>
        <li>Cole numa IA de sua preferência (ChatGPT, Claude, Gemini…).</li>
        <li>Responda às perguntas do briefing sobre o seu negócio.</li>
        <li>
          Cole os textos que ela gerar em cada campo: Instruções (prompt
          principal), Cenários, Proteções e FAQs.
        </li>
      </ol>

      <div class="flex items-center justify-between gap-3">
        <span class="text-sm font-medium text-n-slate-12">Meta-prompt</span>
        <Button
          label="Copiar meta-prompt"
          icon="i-ri-file-copy-line"
          size="sm"
          @click="copyPrompt"
        />
      </div>

      <pre
        class="max-h-80 overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-n-weak bg-n-slate-2 p-4 text-xs text-n-slate-12 font-mono"
        >{{ metaPrompt }}</pre
      >
    </div>
  </Dialog>
</template>
